import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography, Button,
  TextField, InputAdornment, ButtonBase,
} from '@mui/material';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  PRICE_TIERS, tierForSpecs, tierRange, formatCurrency, parsePriceValue, marketValueFor,
} from './productUtils';

/**
 * "Suggested value by condition" — web port of the iOS PriceConditionDrawerView.
 * Tier prices are anchored to the condition-neutral market value
 * (priceOptions.marketValue, see marketValueFor) rather than the current
 * price, so re-picking a tier never compounds a discount.
 *
 * Props: open, onClose, product, onDone(priceString)
 */
export default function PriceConditionDialog({ open, onClose, product, onDone }) {
  const specs = product?.specs || {};
  const marketValue = marketValueFor(product);
  const priceLow = parsePriceValue(specs.PriceLow);
  const priceHigh = parsePriceValue(specs.PriceHigh);

  const [selected, setSelected] = useState(null);
  const [custom, setCustom] = useState('');

  useEffect(() => {
    if (open) {
      setSelected(tierForSpecs(specs).key);
      setCustom('');
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const tierPrice = (tier) => Math.round(marketValue * tier.percentage * 100) / 100;
  const customValue = parseFloat(custom);
  const doneEnabled = selected === 'custom' ? !Number.isNaN(customValue) && customValue >= 0 : !!selected;

  const handleDone = () => {
    const value = selected === 'custom' ? customValue : tierPrice(PRICE_TIERS.find((t) => t.key === selected));
    onDone(value.toFixed(2));
    onClose();
  };

  const Row = ({ id, title, subtitle, right, rightSub }) => {
    const isSelected = selected === id;
    return (
      <ButtonBase
        onClick={() => setSelected(id)}
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          textAlign: 'left',
          gap: 1.5,
          px: 1.75,
          py: 1.25,
          mb: 1,
          borderRadius: 2,
          border: '1px solid',
          borderColor: isSelected ? 'primary.main' : 'divider',
          borderWidth: isSelected ? 1.5 : 1,
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.95rem' }}>{title}</Typography>
          <Typography variant="caption" color="text.secondary">{subtitle}</Typography>
        </Box>
        {right && (
          <Box sx={{ textAlign: 'right' }}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.95rem' }}>{right}</Typography>
            <Typography variant="caption" color="text.secondary">{rightSub}</Typography>
          </Box>
        )}
        {isSelected ? <CheckCircleIcon color="primary" fontSize="small" /> : <RadioButtonUncheckedIcon fontSize="small" sx={{ color: 'text.disabled' }} />}
      </ButtonBase>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 0.5 }}>
        Suggested value by condition
        <Typography variant="body2" color="text.secondary">
          Based on market value of {formatCurrency(marketValue)}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ pt: '12px !important' }}>
        {PRICE_TIERS.map((tier) => {
          const range = tierRange(tier, marketValue, priceLow, priceHigh);
          return (
            <Row
              key={tier.key}
              id={tier.key}
              title={tier.label}
              subtitle={tier.sublabel}
              right={formatCurrency(tierPrice(tier))}
              rightSub={`${formatCurrency(range.low, { whole: true })} – ${formatCurrency(range.high, { whole: true })}`}
            />
          );
        })}
        <Row id="custom" title="Custom value" subtitle="Enter your own price" />
        {selected === 'custom' && (
          <TextField
            autoFocus
            fullWidth
            size="small"
            type="number"
            placeholder="0.00"
            value={custom}
            onChange={(e) => setCustom(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
          />
        )}
        <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 2, textAlign: 'center' }}>
          Values are estimates based on recent marketplace sales and may vary by platform, demand, and item condition.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleDone} disabled={!doneEnabled}>Done</Button>
      </DialogActions>
    </Dialog>
  );
}
